import type { PaigeContentCentralType, PaigeFieldMapping } from './paige.global';

/** The destination form's fields; secrets stay empty on an edit and are only sent when typed. */
export interface Draft {
  label: string;
  url: string;
  secret: string;
  host: string;
  port: string;
  username: string;
  folder: string;
  password: string;
  privateKey: string;
  siteUrl: string;
  catalog: string;
  apiKey: string;
  organization: string;
  project: string;
  classification: string;
  /** A storage destination's chosen place: the picker's id and the path people read. */
  locationId: string;
  locationPath: string;
  /** A storage destination: 'yes' when the data file (.json) is filed beside each PDF, else ''. */
  dataFile: string;
  /** Microsoft 365 and Google Drive: 'yes' when every field also fills the column or property of its own name, else ''. */
  sameNames: string;
  /** The field map; for a storage destination, the fields written as metadata (columns). */
  fieldMap: PaigeFieldMapping[];
  /** Content Central: the type map, one line per Paige document type that does not go by name. */
  types: PaigeContentCentralType[];
}

/** The draft's text fields (everything but the maps). */
export type TextKey = Exclude<keyof Draft, 'fieldMap' | 'types'>;

export type Setter = (key: TextKey) => (event: { target: { value: string } }) => void;

export interface FieldsProps {
  draft: Draft;
  set: Setter;
  /** A secret is stored already: the secret field may stay empty and says so. */
  secretSet: boolean;
  /** The stored destination, once there is one (a product's pickers browse with its stored key). */
  destinationId?: string;
  /** What a successful test listed per setting (catalogs, document types, projects...), offered as suggestions. */
  choices?: Record<string, string[]>;
  setMap?: (map: PaigeFieldMapping[]) => void;
  setTypes?: (types: PaigeContentCentralType[]) => void;
}

/** The destination's name, as the card lists it. */
export function NameField({ draft, set }: FieldsProps) {
  return (
    <label>
      Name
      <input value={draft.label} onChange={set('label')} maxLength={80} required autoFocus />
    </label>
  );
}

/** URL and secret for a webhook destination. */
export function WebhookFields({ draft, set, secretSet }: FieldsProps) {
  return (
    <>
      <label>
        URL
        <input type="url" value={draft.url} onChange={set('url')} placeholder="https://" required />
      </label>
      <label>
        Secret
        <input
          type="password"
          value={draft.secret}
          onChange={set('secret')}
          placeholder={secretSet ? 'Stored; type to replace' : 'At least 16 characters'}
          minLength={16}
          required={!secretSet}
          autoComplete="new-password"
        />
      </label>
    </>
  );
}

/** Host, port, user name, folder, and a password or a private key for an SFTP destination. */
export function SftpFields(props: FieldsProps) {
  return (
    <>
      <SftpHostFields {...props} />
      <SftpCredentialFields {...props} />
    </>
  );
}

function SftpHostFields({ draft, set }: FieldsProps) {
  return (
    <>
      <span className="paige-destination-row">
        <label>
          Host
          <input value={draft.host} onChange={set('host')} required />
        </label>
        <label>
          Port
          <input type="number" min={1} max={65535} value={draft.port} onChange={set('port')} />
        </label>
      </span>
      <label>
        User name
        <input value={draft.username} onChange={set('username')} required autoComplete="off" />
      </label>
      <label>
        Folder
        <input value={draft.folder} onChange={set('folder')} placeholder="/inbound" />
        <small>
          Where the job&apos;s file naming starts: each PDF goes in the folders under it and under
          the name set in Job setup, Export, File naming.
        </small>
      </label>
    </>
  );
}

function SftpCredentialFields({ draft, set, secretSet }: FieldsProps) {
  return (
    <>
      <label>
        Password
        <input
          type="password"
          value={draft.password}
          onChange={set('password')}
          placeholder={secretSet ? 'Stored; type to replace' : ''}
          autoComplete="new-password"
        />
      </label>
      <label>
        Private key (instead of a password)
        <textarea
          value={draft.privateKey}
          onChange={set('privateKey')}
          rows={3}
          placeholder="-----BEGIN ... PRIVATE KEY-----"
        />
      </label>
    </>
  );
}
